<template>
  <div>
    <section class="content-group -bg-01 -center -no-spacer -vs-medium -column">
      <div class="group -flex -between -narrow -vs-small -vs-bottom -vs-collapse-bottom">
        <h1 id="spa-title" class="heading -primary">
          {{ stepTitle }}
        </h1>
        <div class="spa-heading-action">
          <router-link to="/my/settings/energy/calorie">Back</router-link>
        </div>
      </div>
      <div class="group -narrow -vs-small -vs-collapse-top">
        <h2 class="heading -tertiary">{{ stepSubTitle }}</h2>
      </div>

      <!-- Calorie Selector -->
      <div
        class="energy-card group -flex -column -center -narrow -vs-small -hs-medium -bg-08 -only-rounder -stack-space-double -range"
      >
        <div class="range-slider">
          <div class="increment">
            <icon-button
              type="button"
              icon="keyboard_arrow_up"
              :classes="'-ico-only -no-container'"
              label="Increment Calories"
              @click-action="incrementCalories"
            />
          </div>
          <div class="slider-input">
            <input
              v-model.number="dailyCalorieType"
              class="slider"
              type="range"
              name="points"
              min="1"
              max="8"
              step="1"
              aria-label="Energy Needs 1 low - 8 high"
            />
          </div>
          <div class="decrement">
            <icon-button
              type="button"
              icon="keyboard_arrow_down"
              :classes="'-ico-only -no-container'"
              label="Decrement Calories"
              @click-action="decrementCalories"
            />
          </div>
        </div>
        <energy-meter :daily-calorie-type="dailyCalorieType" :calorie-types="calorieTypes" />
        <div class="energy-level">
          {{ calorieTypes[dailyCalorieType].name }}
          <div v-if="isGoalStep" class="heading -quinary -caps calorie-range">
            {{ calorieTypes[dailyCalorieType].calMin }}-{{ calorieTypes[dailyCalorieType].calMax }}
            Calories
          </div>
        </div>
      </div>
      <!-- End Calorie Selector -->

      <!-- Page Action -->
      <div class="spa-action">
        <submit-button
          :submitting="saving"
          label="Save"
          submitting-label="Processing…"
          classes="-main"
          @submit-action="saveEnergyGoal"
        />
      </div>
      <!-- End Page Action -->
    </section>

    <calorie-more-info
      v-if="!isGoalStep"
      :show-calories="isGoalStep"
      title="Which energy range should I choose?"
    >
      <p>
        For most adults, the medium range is a good place to start. If you are on the smaller side
        or only lightly active try medium-low. However, if you have a larger frame or are more
        active, medium-high will provide a little more energy throughout the day. You can update
        your energy range at any time so be mindful about how you are feeling and adjust your
        settings as needed, even right in the middle of an active meal plan!
      </p>
      <p>
        Many factors affect the amount of energy you need each day. The following descriptions
        provide common characteristics for each range, but you may need to mix and match to find the
        right level for your needs. For example, someone with a below average frame and low muscle
        mass might fall into a low range, but if they are also very active they will likely need a
        higher range to have enough energy per day. If you are unsure, start with a medium range and
        adjust your settings as you go based on how you feel.
      </p>
    </calorie-more-info>
    <calorie-more-info v-else :show-calories="isGoalStep" title="How are the ranges organized?">
      <p>
        To give you flexibility within a meal plan, we use a calorie range instead of an exact
        calorie number. The following descriptions provide common characteristics for each range,
        but your specific needs may include characteristics from multiple ranges. You can update
        your energy range at any time so be mindful about how you are feeling and adjust your
        settings as needed, even right in the middle of an active meal plan!
      </p>
    </calorie-more-info>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import EnergyMeter from './energy-meter.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import CalorieMoreInfo from '@/components/users/settings/energy/calorie-more-info.vue'

/**
 * Step 3a and Step 3b - Calorie Range / Goal
 *
 * If route energyType "range"
 * - hide calorie metrics
 *
 * If route energyType "goal"
 * - show calorie metrics
 *
 * Otherwise functionality is the same.
 */
export default defineComponent({
  name: 'CalorieGoalStep',
  components: {
    CalorieMoreInfo,
    IconButton,
    EnergyMeter,
    SubmitButton,
  },
  props: {
    calorieTypes: {
      type: Object,
      required: true,
    },

    initialDailyCalorieType: {
      type: Number,
      default: 4,
    },

    // loading state from parent
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save-action'],
  data() {
    return {
      // initialize from prop, but then self manage
      dailyCalorieType: this.initialDailyCalorieType,
    }
  },

  computed: {
    // based on route energyMethod: goal,range
    stepTitle() {
      if (this.$route.params.energyMethod === 'goal') {
        return 'Calorie Goal'
      }

      return 'General Range'
    },

    stepSubTitle() {
      if (this.$route.params.energyMethod === 'goal') {
        return 'I want to target a specific calorie range:'
      }

      return 'Compared to recommended daily values my needs are:'
    },

    isGoalStep() {
      return this.$route.params.energyMethod === 'goal'
    },
  },

  methods: {
    incrementCalories() {
      if (this.dailyCalorieType < 10) {
        this.dailyCalorieType += 1
      }
    },
    decrementCalories() {
      if (this.dailyCalorieType > 1) {
        this.dailyCalorieType -= 1
      }
    },

    // saves snake_case to make backend easier
    saveEnergyGoal() {
      const formData = {
        energy_method: this.$route.params.energyMethod,
        daily_calorie_type: this.dailyCalorieType,
      }

      // return to step 1
      const nextRoute = {
        path: '/my/settings/energy',
        query: {
          success: 'calorie',
        },
      }

      this.$emit('save-action', { formData, nextRoute })
    },
  },
})
</script>
